import { z } from 'zod'
import { PlacedPartSchema } from '../domain/assembly'
import { AssemblyConnectionSchema } from '../domain/connection'
import { RobotGroupSchema } from '../domain/robot-group'
import { SimulationObstacleSchema } from '../domain/simulation-obstacle'
import {
  CodingProgramBlockSchema,
  DEFAULT_CODING_PROGRAM_NAME,
  createDefaultCodingProgram,
} from '../domain/coding'

const MotorControlSchema = z.object({
  direction: z.enum(['forward', 'stop', 'reverse']),
  speedPercent: z.number().min(0).max(100),
})

const WorkspaceDraftSchema = z.object({
  parts: z.array(PlacedPartSchema),
  connections: z.array(AssemblyConnectionSchema),
  robotGroups: z.array(RobotGroupSchema).default([]),
  motorControls: z.record(z.string(), MotorControlSchema).default({}),
  simulationObstacles: z.array(SimulationObstacleSchema).default([]),
  codingProgramName: z.string().max(30).default(DEFAULT_CODING_PROGRAM_NAME),
  codingProgram: z.array(CodingProgramBlockSchema).default(createDefaultCodingProgram()),
  environment: z.enum(['flat', 'ramp', 'road', 'obstacle']).default('flat'),
})

export type WorkspaceDraft = z.infer<typeof WorkspaceDraftSchema>
export type WorkspaceDraftId = 'autonomous' | 'mission-01'

const ACTIVE_KEY = 'pingpongland:active-workspace:v1'
const draftKey = (id: WorkspaceDraftId) => `pingpongland:workspace:${id}:v1`

function storage() {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export const workspaceDraftRepository = {
  active(): WorkspaceDraftId {
    return storage()?.getItem(ACTIVE_KEY) === 'mission-01' ? 'mission-01' : 'autonomous'
  },

  setActive(id: WorkspaceDraftId) {
    storage()?.setItem(ACTIVE_KEY, id)
  },

  save(id: WorkspaceDraftId, draft: WorkspaceDraft) {
    storage()?.setItem(draftKey(id), JSON.stringify(WorkspaceDraftSchema.parse(draft)))
  },

  load(id: WorkspaceDraftId): WorkspaceDraft | null {
    const raw = storage()?.getItem(draftKey(id))
    if (!raw) return null
    try {
      return WorkspaceDraftSchema.parse(JSON.parse(raw))
    } catch {
      return null
    }
  },
}
